import moment from 'moment'
import PropTypes from 'prop-types'
import { Bar } from 'react-chartjs-2'
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js'
import { useSelector } from 'react-redux'
import globals from '../../../../../assets/styles/globals.scss'
import { displayWithCurrency } from '../../../../../utility/currency'
import { useCallback, useMemo } from 'react'


ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

Tooltip.positioners.cursor = function(elements, coordinates) {
  return { x: coordinates.x, y: coordinates.y }
}

const FinancialPerformanceChart = ({ monthlyData, isRevenue }) => {
  const communityCategories = useSelector(state => state.dashboard.communityCategories)
  const currency = useSelector(state => state.user.current_user.communities[0].currency)

  const currentYear = new Date().getFullYear()

  const labels = useMemo(() => monthlyData.map(item => {
    const date = moment(item.month)
    return date.year() !== currentYear ? date.format('MMM YYYY') : date.format('MMM')
  }), [currentYear, monthlyData])

  const datasets = useMemo(() => [{
    label: 'Total',
    backgroundColor: globals.slateGray,
    hoverBackgroundColor: globals.slateGrayIcons,
    stack: 'stack-0',
    data: monthlyData.map(month => month.courseCategories.reduce((sum, c) => sum + c.value, 0)),
  }], [monthlyData])

  const externalTooltipHandler = useCallback(context => {
    let tooltipEl = document.getElementById('chartjs-tooltip')

    // Create element if not exists
    if (!tooltipEl) {
      tooltipEl = document.createElement('div')
      tooltipEl.id = 'chartjs-tooltip'
      tooltipEl.style.background = '#fff'
      tooltipEl.style.border = `1px solid ${globals.lightGray}`
      tooltipEl.style.padding = '12px 18px'
      tooltipEl.style.borderRadius = '8px'
      tooltipEl.style.boxShadow = '0px 4px 4px rgba(0, 0, 0, 0.2)'
      tooltipEl.style.pointerEvents = 'none'
      tooltipEl.style.position = 'absolute'
      tooltipEl.style.transition = 'all 0.1s ease'
      tooltipEl.style.fontFamily = 'inherit'
      tooltipEl.style.fontSize = '14px'
      document.body.appendChild(tooltipEl)
    }

    const tooltipModel = context.tooltip

    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = 0
      return
    }

    let innerHtml = ''
    const dataIndex = tooltipModel.dataPoints[0].dataIndex

    // Header
    const date = moment.utc(monthlyData[dataIndex].month)
    const currentYear = new Date().getFullYear()

    const title = date.year() === currentYear
      ? date.format('MMMM')
      : date.format('MMMM YYYY')

    innerHtml += `
      <div style="font-weight:800; font-size: 1rem; color:${globals.darkNavy}; margin-bottom:1rem;">
        ${title}
      </div>
      `

    // Body
    const categories = monthlyData[dataIndex].courseCategories

    const total = categories.reduce((s, c) => s + (c.value || 0), 0)

    categories.forEach(cat => {
      if (!cat) {
        return
      }

      const percentage = total ? Math.round((cat.value / total) * 100) : 0
      const formattedValue = isRevenue
        ? displayWithCurrency(cat.value, currency, true)
        : cat.value

      const category = communityCategories.find(c => c.id === cat.id)

      innerHtml += `
        <div style="
          display:grid;
          grid-template-columns: 1fr 60px 60px;
          column-gap: 12px;
          align-items:center;
          margin-bottom:4px;
          font-size: 0.9rem;
        ">
          <div style="display:flex; align-items:center; gap:0.75rem;">
            <span
              style="
                display:inline-block;
                width:8px; height:8px;
                border-radius:50%;
                background:${category.color};
                flex-shrink:0;
              "
            ></span>
            ${category.name}
          </div>
      
          <span style="text-align:right;">${percentage}%</span>
          <span style="text-align:right;font-weight:800;">${formattedValue}</span>
        </div>
      `
    })

    // Total
    const formattedTotal = isRevenue ? displayWithCurrency(total, currency, true) : total

    innerHtml += `
      <div style='display:flex; justify-content:space-between; font-weight:800; font-size: 0.9rem; color:${globals.darkNavy}; margin-top:1rem;'>
        <span>Total ${isRevenue ? 'Revenue' : 'Registrations'}</span>
        <span style='flex-shrink: 0; margin-left: 1rem;'>${formattedTotal}</span>
      </div>
    `

    tooltipEl.innerHTML = innerHtml

    // Position tooltip
    const canvasRect = context.chart.canvas.getBoundingClientRect()
    tooltipEl.style.opacity = 1

    // Get viewport width/height
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Calculate desired position
    let left = canvasRect.left + window.pageXOffset + tooltipModel.caretX + 12
    let top = canvasRect.top + window.pageYOffset + tooltipModel.caretY + 12

    // Clamp tooltip to viewport so it doesn't overflow
    const tooltipRect = tooltipEl.getBoundingClientRect()
    if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width - 8
    }
    if (top + tooltipRect.height > viewportHeight) {
      top = viewportHeight - tooltipRect.height - 8
    }

    tooltipEl.style.left = left + 'px'
    tooltipEl.style.top = top + 'px'
  }, [communityCategories, currency, isRevenue, monthlyData])


  const chartData = {
    labels,
    datasets,
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        // Disable default tooltip
        enabled: false,
        external: externalTooltipHandler,
        position: 'cursor',
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        ticks: {
          precision: 0,
          maxTicksLimit: 5,
          callback: function(value) {
            return isRevenue ? displayWithCurrency(value, currency, true) : value
          },
        },
      },
    },
  }

  return (
    <div className='h-100 w-100'>
      <Bar data={chartData} options={options} />
    </div>
  )
}

FinancialPerformanceChart.propTypes = {
  monthlyData: PropTypes.array.isRequired,
  isRevenue: PropTypes.bool,
}

export default FinancialPerformanceChart
